import { Extension } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { type EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  BRACKET_CLOSE_TO_OPEN,
  BRACKET_OPEN_TO_CLOSE,
  BRACKET_PAIR_CHARS,
} from "./bracket-utils";

const isBracketChar = (c: string): boolean =>
  c.length === 1 && BRACKET_PAIR_CHARS.has(c);

/** O(1) — single-codepoint neighbors of the caret (no full-document walk). */
const neighborCharsAtHead = (
  doc: Node,
  head: number,
): {
  before: string;
  after: string;
} => {
  const before = head > 0 ? doc.textBetween(head - 1, head, "", "\ufffc") : "";
  const after =
    head < doc.content.size
      ? doc.textBetween(head, head + 1, "", "\ufffc")
      : "";
  return { before, after };
};

const matchingBracketPluginKey = new PluginKey<DecorationSet>(
  "matchingBracketHighlight",
);

type FlattenDocResult = {
  text: string;
  starts: number[];
  ibefore: number;
  iafter: number;
};

/**
 * One pass over text leaves: builds `text` via chunk join (avoids per-char
 * string +=), `starts` for PM positions, and cursor-adjacent char indices.
 */
const flattenDocText = (doc: Node, head: number): FlattenDocResult => {
  const starts: number[] = [];
  const chunks: string[] = [];
  let ibefore = -1;
  let iafter = -1;
  let index = 0;

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const t = node.text;
    for (let i = 0; i < t.length; i++) {
      const p = pos + i;
      starts.push(p);
      if (p === head - 1) ibefore = index;
      if (p === head) iafter = index;
      index++;
    }
    chunks.push(t);
  });

  return {
    text: chunks.join(""),
    starts,
    ibefore,
    iafter,
  };
};

/** Binary search: index of character whose PM start equals `docPos`, or -1. */
export const findCharStartIndex = (
  starts: readonly number[],
  docPos: number,
): number => {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = starts[mid];
    if (v === undefined) return -1;
    if (v === docPos) return mid;
    if (v < docPos) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
};

export const matchForwardBrace = (
  text: string,
  openIdx: number,
  openCh: string,
  closeCh: string,
): { open: number; close: number } | null => {
  const openCode = openCh.charCodeAt(0);
  const closeCode = closeCh.charCodeAt(0);
  let depth = 1;
  for (let i = openIdx + 1, len = text.length; i < len; i++) {
    const c = text.charCodeAt(i);
    if (c === openCode) depth++;
    else if (c === closeCode) {
      depth--;
      if (depth === 0) return { open: openIdx, close: i };
    }
  }
  return null;
};

export const matchBackwardBrace = (
  text: string,
  closeIdx: number,
  openCh: string,
  closeCh: string,
): { open: number; close: number } | null => {
  const openCode = openCh.charCodeAt(0);
  const closeCode = closeCh.charCodeAt(0);
  let depth = 1;
  for (let i = closeIdx - 1; i >= 0; i--) {
    const c = text.charCodeAt(i);
    if (c === closeCode) depth++;
    else if (c === openCode) {
      depth--;
      if (depth === 0) return { open: i, close: closeIdx };
    }
  }
  return null;
};

type HeadCharIndices = { ibefore: number; iafter: number };

export const findBracketPairAtHead = (
  text: string,
  starts: readonly number[],
  head: number,
  resolved?: HeadCharIndices,
): { open: number; close: number } | null => {
  const ibefore =
    resolved?.ibefore ?? (head > 0 ? findCharStartIndex(starts, head - 1) : -1);
  const iafter = resolved?.iafter ?? findCharStartIndex(starts, head);

  const tryBefore = (): { open: number; close: number } | null => {
    if (ibefore < 0) return null;
    const cb = text.charAt(ibefore);
    const close = BRACKET_OPEN_TO_CLOSE[cb];
    if (close) return matchForwardBrace(text, ibefore, cb, close);
    const open = BRACKET_CLOSE_TO_OPEN[cb];
    if (open) return matchBackwardBrace(text, ibefore, open, cb);
    return null;
  };

  const tryAfter = (): { open: number; close: number } | null => {
    if (iafter < 0) return null;
    const ca = text.charAt(iafter);
    const close = BRACKET_OPEN_TO_CLOSE[ca];
    if (close) return matchForwardBrace(text, iafter, ca, close);
    const open = BRACKET_CLOSE_TO_OPEN[ca];
    if (open) return matchBackwardBrace(text, iafter, open, ca);
    return null;
  };

  return tryBefore() ?? tryAfter();
};

const buildDecorationSet = (state: EditorState): DecorationSet => {
  const { doc, selection } = state;
  const head = selection.head;
  if (head < 1 || head > doc.content.size) {
    return DecorationSet.empty;
  }

  const { before, after } = neighborCharsAtHead(doc, head);
  if (!isBracketChar(before) && !isBracketChar(after)) {
    return DecorationSet.empty;
  }

  const { text, starts, ibefore, iafter } = flattenDocText(doc, head);
  if (text.length === 0) return DecorationSet.empty;

  const pair = findBracketPairAtHead(text, starts, head, {
    ibefore,
    iafter,
  });
  if (!pair) return DecorationSet.empty;

  const openPos = starts[pair.open];
  const closePos = starts[pair.close];
  if (openPos === undefined || closePos === undefined) {
    return DecorationSet.empty;
  }
  return DecorationSet.create(doc, [
    Decoration.inline(openPos, openPos + 1, {
      class: "pm-matching-bracket",
    }),
    Decoration.inline(closePos, closePos + 1, {
      class: "pm-matching-bracket",
    }),
  ]);
};

const matchingBracketPlugin = new Plugin<DecorationSet>({
  key: matchingBracketPluginKey,
  state: {
    init: (_, state) => buildDecorationSet(state),
    apply(tr, prev, oldState, newState) {
      if (!tr.docChanged && oldState.selection.eq(newState.selection)) {
        return prev.map(tr.mapping, tr.doc);
      }
      return buildDecorationSet(newState);
    },
  },
  props: {
    decorations(state) {
      return matchingBracketPluginKey.getState(state);
    },
  },
});

/**
 * Highlights the matching bracket pair adjacent to the caret using view-only
 * decorations. Common case: O(1) neighbor check with no flatten. When a
 * bracket neighbors the caret: one O(n) flatten (chunked join + single pass
 * for indices) and O(distance) scan for the partner pair.
 */
const MatchingBracketHighlight = Extension.create({
  name: "matchingBracketHighlight",

  addProseMirrorPlugins() {
    return [matchingBracketPlugin];
  },
});

export default MatchingBracketHighlight;
